// 입고 CAPA 모니터링 — «SKU 적재 기준» 탭. 화주·상품별 박스 입수(EA) × PLT당 박스 수 = 1 PLT EA.
//   · 표(검색·페이지) · 추가 · 편집 · 삭제 · 양식 / 현재 기준 내려받기 · 엑셀 올리기(저장·갱신·실패 행 표)
//   · 미등록 SKU 목록(마지막 성공 판정에서 기준 없이 추정·제외된 상품)에서 바로 등록.
//     목록은 판정 결과라 다음 판정 전에는 서버가 같은 줄을 준다 → 바로 등록한 줄은 화면에서만 뺀다(다시 읽지 않는다).
// 🔴 쓰기(추가·편집·삭제·올리기·등록)는 /me can_manage 일 때만 — 아니면 읽기 전용(단추·입력 disabled, 감추지 않는다).
//    실제 차단은 서버다(403 문구를 그대로 보인다).
// 🔴 1 PLT EA 는 곱을 보여 주기만 한다(저장하지 않는다) · 환산 공식은 서버가 정본이다(여기서 다시 계산하지 않는다).
// 🔴 포장단위 참고값(refs)은 WMS 숫자 컬럼을 그대로 보인다 — 화면 참고용이고 저장하지 않는다.
// 🔴 내려받기는 blob 으로 받아 저장한다(링크로 열면 토큰이 안 실린다) · 오류 본문도 Blob 이라 읽어서 detail 을 보인다.
import { Fragment, useEffect, useRef, useState } from 'react';
import { inboundCapaAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { fmtKstDateTime, fmtKstShort, kstToday } from '../../utils/kstTime';
import { COLORS, badge, btn, card, input } from '../../utils/uiStyles';
import { dayLabel, errorText, readBlobText } from './MonitorTab';
import { toIntOrRaw } from './SettingsTab';

export const PAGE_SIZE = 50;
export const REGISTERED_NOTE = '등록했습니다 — 다음 판정(또는 지금 새로고침)부터 반영됩니다';
const SAVED_NOTE = '저장했습니다 — 다음 판정(또는 지금 새로고침)부터 반영됩니다';
const UPLOADED_NOTE = '올렸습니다 — 다음 판정(또는 지금 새로고침)부터 반영됩니다';
const READONLY = '읽기 전용 — SKU 적재 기준은 운영·시스템 관리자 또는 설정 관리 조직만 고칠 수 있습니다';
const NO_CHANGES = '바뀐 항목이 없습니다';
export const TEMPLATE_NAME = '입고CAPA_SKU기준_양식.xlsx';
const DASH = '—';
const EMPTY_FORM = { pwn_cd: '', prod_cd: '', box_ea: '', boxes_per_plt: '', memo: '' };
const MODE_NOTES = {
  estimate: '미등록 SKU 는 지금 포장단위 박스입수(없으면 1) × PLT당 기본 박스 수로 추정 환산합니다(카드에 «추정 포함»)',
  exclude: '미등록 SKU 는 지금 PLT 합계에서 뺍니다(EA 합계에는 들어갑니다)',
};

const section = { ...card, padding: '12px 14px', marginBottom: '12px', overflowWrap: 'anywhere' };
const sectionHead = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px', marginBottom: '8px' };
const sectionTitle = { margin: 0, fontSize: '15px' };
const subTitle = { margin: '0 0 8px', fontSize: '13px' };
const mutedLine = { color: COLORS.textSub, fontSize: '12px' };
const alertLine = { color: COLORS.dangerText, fontSize: '12px' };
const okLine = { color: COLORS.successText, fontSize: '12px' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '10px 12px' };
const labelStyle = { display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '4px' };
const fieldInput = { ...input, padding: '6px 9px', fontSize: '13px' };
const cellInput = { ...input, padding: '4px 6px', fontSize: '13px' };
const th = { padding: '8px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '6px 8px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const actions = { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px', alignItems: 'center' };
const subBox = { background: COLORS.bg, border: `1px solid ${COLORS.borderSoft}`, padding: '10px 12px', borderRadius: '8px' };
const disabledLook = (off) => (off ? { opacity: 0.55, cursor: 'not-allowed' } : {});

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const fmt = (v) => (isNum(v) ? v.toLocaleString('ko-KR') : DASH);
const numText = (v) => (isNum(v) ? String(v) : '');
const keyOf = (x) => `${x.pwn_cd}|${x.prod_cd}`;

// 1 PLT EA = 박스 입수 × PLT당 박스 수 — 보여 주기만 한다. 둘 중 하나라도 숫자가 아니면 —.
export function pltEa(boxEa, boxesPerPlt) {
  const a = typeof boxEa === 'number' ? boxEa : toIntOrRaw(boxEa);
  const b = typeof boxesPerPlt === 'number' ? boxesPerPlt : toIntOrRaw(boxesPerPlt);
  return isNum(a) && isNum(b) && a > 0 && b > 0 ? a * b : null;
}

// 포장단위 참고값 — [{컬럼: 값}] → '컬럼 값 · 컬럼 값' (여러 행이면 ' / ').
export function refsText(refs) {
  const rows = (Array.isArray(refs) ? refs : []).filter(isObj)
    .map((r) => Object.entries(r).map(([k, v]) => `${k} ${v === null || v === undefined ? DASH : v}`).join(' · '))
    .filter(Boolean);
  return rows.length ? rows.join(' / ') : '';
}

// 등록·추가 본문 — 메모는 비우면 넣지 않는다(서버가 null 로 둔다). 숫자 판정은 서버 문구로 보인다.
function skuBody(f) {
  const body = {
    pwn_cd: String(f.pwn_cd || '').trim(),
    prod_cd: String(f.prod_cd || '').trim(),
    box_ea: toIntOrRaw(f.box_ea),
    boxes_per_plt: toIntOrRaw(f.boxes_per_plt),
  };
  const memo = String(f.memo || '').trim();
  if (memo) body.memo = memo;
  return body;
}

async function blobErrorText(err, fallback) {
  const message = errorText(err, '');
  if (message) return message;
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await readBlobText(data));
      if (typeof parsed?.detail === 'string' && parsed.detail) return parsed.detail;
    } catch (e) { /* 기본 문구를 쓴다 */ }
  }
  return fallback;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Msg({ msg, testId }) {
  if (!msg) return null;
  const style = msg.kind === 'error' ? alertLine : msg.kind === 'ok' ? okLine : mutedLine;
  return (
    <div data-testid={testId} data-kind={msg.kind} role={msg.kind === 'error' ? 'alert' : 'status'} style={{ ...style, marginTop: '6px' }}>
      {msg.text}
    </div>
  );
}

function SkuFields({ idBase, form, onChange, off, withCodes }) {
  const field = (name, label, extra = {}) => (
    <div style={{ minWidth: 0 }}>
      <label htmlFor={`${idBase}-${name}`} style={labelStyle}>{label}</label>
      <input id={`${idBase}-${name}`} type="text" value={form[name]} disabled={off}
        onChange={(ev) => onChange(name, ev.target.value)} style={{ ...fieldInput, ...disabledLook(off) }} {...extra} />
    </div>
  );
  const product = pltEa(form.box_ea, form.boxes_per_plt);
  return (
    <div style={grid}>
      {withCodes && field('pwn_cd', '화주코드', { maxLength: 20 })}
      {withCodes && field('prod_cd', '상품코드', { maxLength: 50 })}
      {field('box_ea', '박스 입수(EA)', { inputMode: 'numeric' })}
      {field('boxes_per_plt', 'PLT당 박스 수', { inputMode: 'numeric' })}
      {field('memo', '메모(선택)', { maxLength: 200 })}
      <div style={{ minWidth: 0, fontSize: '13px', paddingTop: '20px' }}>
        1 PLT = <b data-testid={`${idBase}-plt-ea`}>{product === null ? DASH : `${fmt(product)} EA`}</b>
      </div>
    </div>
  );
}

export default function SkuTab({ me, focus = '' }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState(null);            // null | { items, total, can_manage }
  const [listMsg, setListMsg] = useState('');
  const [editing, setEditing] = useState(null);      // null | { id, box_ea, boxes_per_plt, memo }
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [msgs, setMsgs] = useState({});
  const [upload, setUpload] = useState(null);        // 올리기 응답
  const [unreg, setUnreg] = useState(null);          // null | { items, total, truncated, run, unregistered_mode }
  const [unregMsg, setUnregMsg] = useState('');
  const [reg, setReg] = useState(null);              // null | { key, ...EMPTY_FORM }
  const [busy, setBusy] = useState('');
  const mountedRef = useRef(true);
  const busyRef = useRef('');
  const listSeqRef = useRef(0);
  const unregRef = useRef(null);

  const canManage = me ? !!me.can_manage : !!list?.can_manage;
  const off = !canManage || !!busy;

  const setMsg = (key, kind, text) => setMsgs((cur) => {
    const next = { ...cur };
    if (kind) next[key] = { kind, text };
    else delete next[key];
    return next;
  });

  const run = async (key, fn) => {
    if (busyRef.current) return;
    busyRef.current = key;
    setBusy(key);
    try {
      await fn();
    } finally {
      busyRef.current = '';
      if (mountedRef.current) setBusy('');
    }
  };

  const loadList = async (term = query, from = offset) => {
    const seq = listSeqRef.current + 1;
    listSeqRef.current = seq;
    try {
      const params = { limit: PAGE_SIZE, offset: from };
      if (term) params.q = term;
      const r = await inboundCapaAPI.skus(params);
      if (!mountedRef.current || seq !== listSeqRef.current) return;
      const data = isObj(r?.data) ? r.data : {};
      setList({
        items: Array.isArray(data.items) ? data.items.filter(isObj) : [],
        total: isNum(data.total) ? data.total : 0,
        can_manage: !!data.can_manage,
      });
      setListMsg('');
    } catch (err) {
      if (!mountedRef.current || seq !== listSeqRef.current) return;
      setList((cur) => cur || { items: [], total: 0, can_manage: false });
      setListMsg(errorText(err, 'SKU 적재 기준을 불러오지 못했습니다.'));
    }
  };

  const loadUnreg = async () => {
    try {
      const r = await inboundCapaAPI.skuUnregistered();
      if (!mountedRef.current) return;
      const data = isObj(r?.data) ? r.data : {};
      setUnreg({
        items: Array.isArray(data.items) ? data.items.filter(isObj) : [],
        total: isNum(data.total) ? data.total : 0,
        truncated: !!data.truncated,
        run: isObj(data.run) ? data.run : null,
        unregistered_mode: data.unregistered_mode || '',
      });
      setUnregMsg('');
    } catch (err) {
      if (!mountedRef.current) return;
      setUnreg((cur) => cur || { items: [], total: 0, truncated: false, run: null, unregistered_mode: '' });
      setUnregMsg(errorText(err, '미등록 SKU 를 불러오지 못했습니다.'));
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadUnreg();
    return () => { mountedRef.current = false; listSeqRef.current += 1; };
  }, []);

  useEffect(() => { loadList(query, offset); }, [query, offset]);

  // 모니터링 «미등록 SKU N개»에서 왔으면 목록이 그려진 뒤 그 자리로 옮긴다.
  useEffect(() => {
    if (focus !== 'unregistered' || !unreg) return;
    const el = unregRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
  }, [focus, unreg]);

  const search = (ev) => {
    ev.preventDefault();
    setEditing(null);
    const term = q.trim();
    if (term === query && offset === 0) {
      loadList(term, 0);                 // 같은 검색어로 다시 누르면 다시 읽는다
      return;
    }
    setOffset(0);
    setQuery(term);
  };

  // ── 추가 · 편집 · 삭제 ──
  const addSku = () => run('add', async () => {
    if (!canManage) return;
    setMsg('add', null);
    try {
      await inboundCapaAPI.createSku(skuBody(addForm));
      if (!mountedRef.current) return;
      setAddForm(EMPTY_FORM);
      setMsg('add', 'ok', SAVED_NOTE);
      await loadList(query, offset);
    } catch (err) {
      if (mountedRef.current) setMsg('add', 'error', errorText(err, 'SKU 적재 기준을 저장하지 못했습니다.'));
    }
  });

  const startEdit = (row) => {
    if (!canManage) return;
    setEditing({ id: row.id, box_ea: numText(row.box_ea), boxes_per_plt: numText(row.boxes_per_plt), memo: row.memo || '' });
    setMsg('row', null);
  };

  const saveEdit = (row) => run(`edit-${row.id}`, async () => {
    if (!canManage || !editing || editing.id !== row.id) return;
    const changes = {};
    const boxEa = toIntOrRaw(editing.box_ea);
    if (boxEa !== row.box_ea) changes.box_ea = boxEa;
    const bpp = toIntOrRaw(editing.boxes_per_plt);
    if (bpp !== row.boxes_per_plt) changes.boxes_per_plt = bpp;
    const memo = editing.memo.trim();
    if (memo !== (row.memo || '')) changes.memo = memo;
    if (Object.keys(changes).length === 0) {
      setEditing(null);
      setMsg('row', 'info', NO_CHANGES);
      return;
    }
    setMsg('row', null);
    try {
      const r = await inboundCapaAPI.updateSku(row.id, changes);
      if (!mountedRef.current) return;
      const fresh = isObj(r?.data) && r.data.id === row.id ? r.data : null;
      if (fresh) setList((cur) => (cur ? { ...cur, items: cur.items.map((x) => (x.id === row.id ? fresh : x)) } : cur));
      setEditing(null);
      setMsg('row', 'ok', SAVED_NOTE);
    } catch (err) {
      if (mountedRef.current) setMsg('row', 'error', errorText(err, 'SKU 적재 기준을 저장하지 못했습니다.'));
    }
  });

  const deleteRow = (row) => run(`del-${row.id}`, async () => {
    if (!canManage) return;
    const ok = await confirmDialog(`${row.pwn_cd} · ${row.prod_cd} 기준을 지웁니다. 다음 판정부터 미등록 SKU 로 셉니다. 계속할까요?`);
    if (!ok || !mountedRef.current) return;
    setMsg('row', null);
    try {
      await inboundCapaAPI.deleteSku(row.id);
    } catch (err) {
      if (err?.response?.status !== 404) {
        if (mountedRef.current) setMsg('row', 'error', errorText(err, '지우지 못했습니다.'));
        return;
      }
    }
    if (!mountedRef.current) return;
    if (editing && editing.id === row.id) setEditing(null);
    setMsg('row', 'ok', SAVED_NOTE);
    await loadList(query, offset);
  });

  // ── 내려받기 · 올리기 ──
  const download = (kind) => run(`download-${kind}`, async () => {
    setMsg('file', null);
    const template = kind === 'template';
    try {
      const r = await (template ? inboundCapaAPI.skuTemplate() : inboundCapaAPI.skuExport());
      if (!mountedRef.current) return;
      saveBlob(r.data, template ? TEMPLATE_NAME : `입고CAPA_SKU기준_${(kstToday() || '').replace(/-/g, '')}.xlsx`);
    } catch (err) {
      const text = await blobErrorText(err, '');
      if (mountedRef.current) setMsg('file', 'error', `내려받기에 실패했습니다${text ? `: ${text}` : '.'}`);
    }
  });

  const onFile = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!file || !canManage) return;
    run('upload', async () => {
      setMsg('file', null);
      setUpload(null);
      try {
        const r = await inboundCapaAPI.skuImport(file);
        if (!mountedRef.current) return;
        const data = isObj(r?.data) ? r.data : {};
        setUpload({
          total: isNum(data.total) ? data.total : 0,
          saved: isNum(data.saved) ? data.saved : 0,
          created: isNum(data.created) ? data.created : 0,
          updated: isNum(data.updated) ? data.updated : 0,
          unchanged: isNum(data.unchanged) ? data.unchanged : 0,
          errors: Array.isArray(data.errors) ? data.errors.filter(isObj) : [],
        });
        if (data.saved > 0) setMsg('file', 'ok', UPLOADED_NOTE);
        await loadList(query, offset);
      } catch (err) {
        if (mountedRef.current) setMsg('file', 'error', `올리기에 실패했습니다: ${errorText(err, '파일을 확인해 주세요.')}`);
      }
    });
  };

  // ── 미등록 SKU 바로 등록 ──
  const startReg = (x) => {
    if (!canManage) return;
    setReg({ key: keyOf(x), pwn_cd: x.pwn_cd, prod_cd: x.prod_cd, box_ea: '', boxes_per_plt: '', memo: '' });
    setMsg('reg', null);
  };

  const saveReg = () => run('reg', async () => {
    if (!canManage || !reg) return;
    setMsg('reg', null);
    try {
      await inboundCapaAPI.createSku(skuBody(reg));
      if (!mountedRef.current) return;
      const key = reg.key;
      setUnreg((cur) => (cur ? { ...cur, items: cur.items.filter((x) => keyOf(x) !== key), total: Math.max(0, cur.total - 1) } : cur));
      setReg(null);
      setMsg('unreg', 'ok', REGISTERED_NOTE);
      await loadList(query, offset);
    } catch (err) {
      if (mountedRef.current) setMsg('reg', 'error', errorText(err, '등록하지 못했습니다.'));
    }
  });

  // ── 그리기 ──
  const items = list?.items || [];
  const total = list?.total || 0;
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + items.length, total);

  const renderRow = (row) => {
    const isEditing = !!editing && editing.id === row.id;
    const product = isEditing ? pltEa(editing.box_ea, editing.boxes_per_plt) : pltEa(row.box_ea, row.boxes_per_plt);
    const setEdit = (field, value) => setEditing((cur) => (cur ? { ...cur, [field]: value } : cur));
    return (
      <tr key={row.id} data-testid="ic-sku-row" data-sku-id={row.id} data-key={keyOf(row)}>
        <td style={td}>{row.pwn_cd}</td>
        <td style={td}>{row.prod_cd}</td>
        <td style={td}>
          {isEditing ? (
            <input aria-label="박스 입수(EA)" type="text" inputMode="numeric" value={editing.box_ea} disabled={off}
              onChange={(ev) => setEdit('box_ea', ev.target.value)} style={{ ...cellInput, width: '80px' }} />
          ) : fmt(row.box_ea)}
        </td>
        <td style={td}>
          {isEditing ? (
            <input aria-label="PLT당 박스 수" type="text" inputMode="numeric" value={editing.boxes_per_plt} disabled={off}
              onChange={(ev) => setEdit('boxes_per_plt', ev.target.value)} style={{ ...cellInput, width: '70px' }} />
          ) : fmt(row.boxes_per_plt)}
        </td>
        <td style={td} data-testid="ic-sku-plt-ea">{product === null ? DASH : fmt(product)}</td>
        <td style={td}>
          {isEditing ? (
            <input aria-label="메모" type="text" maxLength={200} value={editing.memo} disabled={off}
              onChange={(ev) => setEdit('memo', ev.target.value)} style={{ ...cellInput, width: '140px' }} />
          ) : (row.memo || DASH)}
        </td>
        <td style={td}>
          <div>{row.updater_name || DASH}</div>
          <div style={mutedLine}>{fmtKstShort(row.updated_at) || ''}</div>
        </td>
        <td style={td}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {isEditing ? (
              <>
                <button type="button" onClick={() => saveEdit(row)} disabled={off} style={{ ...btn('primary', 'sm'), ...disabledLook(off) }}>저장</button>
                <button type="button" onClick={() => setEditing(null)} style={btn('secondary', 'sm')}>취소</button>
              </>
            ) : (
              <button type="button" onClick={() => startEdit(row)} disabled={off} style={{ ...btn('secondary', 'sm'), ...disabledLook(off) }}>편집</button>
            )}
            <button type="button" onClick={() => deleteRow(row)} disabled={off} style={{ ...btn('danger', 'sm'), ...disabledLook(off) }}>삭제</button>
          </div>
        </td>
      </tr>
    );
  };

  const renderUnregRow = (x) => {
    const key = keyOf(x);
    const open = !!reg && reg.key === key;
    const refs = refsText(x.refs);
    return (
      <Fragment key={key}>
        <tr data-testid="ic-sku-unreg-row" data-key={key}>
          <td style={td}>{x.pwn_nm ? `${x.pwn_nm} (${x.pwn_cd})` : x.pwn_cd}</td>
          <td style={td}>{x.prod_cd}</td>
          <td style={td}>{fmt(x.ea)}</td>
          <td style={td}>{fmt(x.lines)}</td>
          <td style={td}>{(x.units || []).join(' · ') || DASH}</td>
          <td style={td}>{x.first_date ? dayLabel(x.first_date) : DASH}</td>
          <td style={{ ...td, color: COLORS.textSub, fontSize: '12px' }} data-testid="ic-sku-unreg-refs">{refs || DASH}</td>
          <td style={td}>
            <button type="button" onClick={() => (open ? setReg(null) : startReg(x))} disabled={off} aria-expanded={open}
              style={{ ...btn(open ? 'secondary' : 'primary', 'sm'), ...disabledLook(off) }}>{open ? '닫기' : '등록'}</button>
          </td>
        </tr>
        {open && (
          <tr>
            <td colSpan={8} style={td}>
              <div data-testid="ic-sku-reg-form" data-key={key} style={subBox}>
                <h4 style={subTitle}>바로 등록 — {x.pwn_cd} · {x.prod_cd}</h4>
                {refs && <div style={{ ...mutedLine, marginBottom: '6px' }}>포장단위 참고값(WMS · 저장하지 않음): {refs}</div>}
                <SkuFields idBase="ic-sku-reg" form={reg} off={off} withCodes={false}
                  onChange={(field, value) => setReg((cur) => (cur ? { ...cur, [field]: value } : cur))} />
                <div style={actions}>
                  <button type="button" onClick={saveReg} disabled={off} style={{ ...btn('primary', 'sm'), ...disabledLook(off) }}>등록 저장</button>
                </div>
                <Msg msg={msgs.reg} testId="ic-sku-reg-msg" />
              </div>
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const unregItems = unreg?.items || [];

  return (
    <>
      <section data-testid="ic-sku-rules" aria-labelledby="ic-sku-title" style={section}>
        <div style={sectionHead}>
          <h3 id="ic-sku-title" style={sectionTitle}>SKU 적재 기준</h3>
          <span style={mutedLine}>PLT 환산 = ⌈⌈수량 / 박스 입수⌉ / PLT당 박스 수⌉ · 1 PLT EA 는 두 값의 곱(보여 주기만 합니다)</span>
          {!canManage && <span data-testid="ic-sku-readonly" style={mutedLine}>{READONLY}</span>}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
          <form onSubmit={search} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input aria-label="SKU 검색" type="search" placeholder="화주코드·상품코드·메모" value={q} maxLength={50}
              onChange={(ev) => setQ(ev.target.value)} style={{ ...fieldInput, width: '220px' }} />
            <button type="submit" style={btn('secondary', 'sm')}>검색</button>
          </form>
          <span style={{ flex: 1 }} />
          <button type="button" data-testid="ic-sku-template" onClick={() => download('template')} disabled={!!busy}
            style={{ ...btn('secondary', 'sm'), ...disabledLook(!!busy) }}>양식 내려받기</button>
          <button type="button" data-testid="ic-sku-export" onClick={() => download('export')} disabled={!!busy}
            style={{ ...btn('secondary', 'sm'), ...disabledLook(!!busy) }}>현재 기준 내려받기</button>
          <label htmlFor="ic-sku-file" style={{ ...btn('accent', 'sm'), ...disabledLook(off), display: 'inline-block' }}>
            {busy === 'upload' ? '올리는 중…' : '엑셀 올리기'}
          </label>
          <input id="ic-sku-file" data-testid="ic-sku-file" type="file" aria-label="SKU 기준 엑셀 파일"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={off} onChange={onFile}
            style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden' }} />
        </div>
        <div style={mutedLine}>양식이나 «현재 기준»을 고쳐 그대로 올리면 됩니다 — 같은 화주·상품이 있으면 고치고, 오류 행은 건너뛰고 나머지를 저장합니다</div>
        <Msg msg={msgs.file} testId="ic-sku-file-msg" />

        {upload && (
          <div data-testid="ic-sku-upload-result" data-saved={upload.saved} data-created={upload.created}
            data-updated={upload.updated} data-failed={upload.errors.length} style={{ ...subBox, marginTop: '8px' }}>
            <div style={{ fontSize: '13px' }}>
              전체 {fmt(upload.total)}행 · 저장 <b>{fmt(upload.created)}</b> · 갱신 <b>{fmt(upload.updated)}</b>
              {' '}· 변경 없음 {fmt(upload.unchanged)} · 실패 <b style={{ color: upload.errors.length ? COLORS.dangerText : undefined }}>{fmt(upload.errors.length)}</b>
            </div>
            {upload.errors.length > 0 && (
              <div style={{ overflowX: 'auto', marginTop: '6px' }}>
                <table data-testid="ic-sku-upload-errors" aria-label="실패한 행" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '420px' }}>
                  <thead><tr><th style={th}>엑셀 행</th><th style={th}>사유</th></tr></thead>
                  <tbody>
                    {upload.errors.map((e, i) => (
                      <tr key={`${e.row}-${i}`} data-testid="ic-sku-upload-error" data-row={e.row}>
                        <td style={td}>{e.row}</td>
                        <td style={{ ...td, color: COLORS.dangerText }}>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div style={{ ...subBox, marginTop: '10px' }} data-testid="ic-sku-add">
          <h4 style={subTitle}>기준 추가</h4>
          <SkuFields idBase="ic-sku-add" form={addForm} off={off} withCodes
            onChange={(field, value) => setAddForm((cur) => ({ ...cur, [field]: value }))} />
          <div style={actions}>
            <button type="button" onClick={addSku} disabled={off} style={{ ...btn('primary', 'sm'), ...disabledLook(off) }}>추가</button>
          </div>
          <Msg msg={msgs.add} testId="ic-sku-add-msg" />
        </div>

        {listMsg && <div role="alert" style={{ ...alertLine, marginTop: '8px' }}>{listMsg}</div>}
        <Msg msg={msgs.row} testId="ic-sku-row-msg" />
        {list === null && <div role="status" style={{ ...mutedLine, marginTop: '8px' }}>불러오는 중…</div>}
        {list !== null && (
          <>
            <div style={{ overflowX: 'auto', marginTop: '10px' }}>
              <table data-testid="ic-sku-table" aria-label="SKU 적재 기준" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                <thead>
                  <tr>{['화주코드', '상품코드', '박스 입수(EA)', 'PLT당 박스 수', '1 PLT EA', '메모', '수정', '동작'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {items.map(renderRow)}
                  {items.length === 0 && (
                    <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>{query ? '찾은 기준이 없습니다' : '등록된 기준이 없습니다'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '12px', color: COLORS.textSub }}>
              <span data-testid="ic-sku-range">{`${fmt(first)}–${fmt(last)} / ${fmt(total)}`}</span>
              <button type="button" onClick={() => { setEditing(null); setOffset(Math.max(0, offset - PAGE_SIZE)); }} disabled={offset === 0}
                style={{ ...btn('secondary', 'sm'), ...disabledLook(offset === 0) }}>이전</button>
              <button type="button" onClick={() => { setEditing(null); setOffset(offset + PAGE_SIZE); }} disabled={offset + PAGE_SIZE >= total}
                style={{ ...btn('secondary', 'sm'), ...disabledLook(offset + PAGE_SIZE >= total) }}>다음</button>
            </div>
          </>
        )}
      </section>

      <section ref={unregRef} data-testid="ic-sku-unregistered" data-focus={focus === 'unregistered' ? 'true' : 'false'}
        aria-labelledby="ic-sku-unreg-title" style={section}>
        <div style={sectionHead}>
          <h3 id="ic-sku-unreg-title" style={sectionTitle}>미등록 SKU</h3>
          {unreg && <span style={badge(unregItems.length ? 'warn' : 'success')}>{fmt(unreg.total)}개</span>}
          {unreg?.run?.finished_at && <span style={mutedLine}>{fmtKstDateTime(unreg.run.finished_at)} 판정 기준</span>}
        </div>
        {unreg?.unregistered_mode && MODE_NOTES[unreg.unregistered_mode] && (
          <div style={{ ...mutedLine, marginBottom: '6px' }}>{MODE_NOTES[unreg.unregistered_mode]} · 등록하면 다음 판정(또는 지금 새로고침)부터 기준으로 환산합니다</div>
        )}
        {unreg?.truncated && <div style={{ ...mutedLine, marginBottom: '6px' }}>많아서 예정 EA 가 큰 순서로 앞쪽만 보입니다</div>}
        {unregMsg && <div role="alert" style={alertLine}>{unregMsg}</div>}
        <Msg msg={msgs.unreg} testId="ic-sku-unreg-msg" />
        {unreg === null && <div role="status" style={mutedLine}>불러오는 중…</div>}
        {unreg !== null && (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="미등록 SKU" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
              <thead>
                <tr>{['화주', '상품코드', '예정 EA', '줄', '판정 단위', '첫 입고예정일', '포장단위 참고값', '동작'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {unregItems.map(renderUnregRow)}
                {unregItems.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>미등록 SKU 가 없습니다</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
